import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styles from './PMSConfiguaration.module.css';
import iconClose from '../../../../images/icon-close.svg';
import iconYardi from '../../../../images/yardi-pms-icon.svg';
import iconEntrata from '../../../../images/entrata-pms-icon.svg';
import RealPagePMSIcon from '../../../svgIcons/RealPagePMSIcon';
import FunnelPMSIcon from '../../../svgIcons/FunnelPMSIcon';
import KnockPMSIcon from '../../../svgIcons/KnowkPmsIcon';
import iconResman from '../../../../images/resman-pms-icon.svg';
import eyeOpen from '../../../../images/icon-eye-open.svg';
import eyeClose from '../../../../images/icon-eye-close.svg';
import successIcon from '../../../../images/successfullyIcon.svg';
import failedIcon from '../../../../images/failedIcon.svg';
import loadingConnecting from '../../../../images/loadingConnecting.svg';
import checkDone from '../../../../images/checkDone.svg';
import loadingDone from '../../../../images/loadingDone.svg';
import Button from '../../../common/button/Button';
import { YardiFormState, EntrataFormState, RealpageFormState, ResmanFormState, KnockFormState, FunnelFormState, PmsConfigSaveData } from './type';
import EntrataPMSIcon from '../../../svgIcons/EntrataPMSIcon';
import YardiPMSIcon from '../../../svgIcons/YardiPMSIcon';
import ResmanPMSIcon from '../../../svgIcons/ResmanPMSIcon';
import { connectPms, ConnectPmsPayload, ConnectPmsKnockPayload } from '../../../../redux/pms-connect/actions/pms-connect.action';
import { AppDispatch } from '../../../../redux/rootReducer';
import { useNavigate } from 'react-router-dom';
import { trimData } from '../../../../utils/trim-data';
import { getLocationById } from '../../../../redux/location/actions/location.action';

type PmsTypeValue = 'yardi' | 'entrata' | 'realpage' | 'resman' | 'knock' | 'funnel';

type PMSConfiguarationProps = {
    setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    onSave?: (data: PmsConfigSaveData) => void;
    editPmsKey?: PmsTypeValue | null;
    editFormData?: Record<string, string> | null;
    /** PMS keys already connected for this property - show greyed out and not selectable in step 0 */
    connectedPmsKeys?: string[];
    locationId?: string;
};

type Step = 0 | 1 | 2;
type PmsType = 'yardi' | 'entrata' | 'realpage' | 'resman' | 'knock' | 'funnel';

const PMSConfiguaration = ({ setOpen, onSave, editPmsKey, editFormData, connectedPmsKeys = [], locationId }: PMSConfiguarationProps) => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const steps = useMemo(() => ['Select PMS/CRM', 'PMS/CRM Info', 'Sync PMS/CRM'] as const, []);
    const [showPass, setShowPass] = useState(false);
    const [step, setStep] = useState<Step>(0);
    const [pmsType, setPmsType] = useState<PmsType | null>(null);
    const passwordRef = useRef<HTMLInputElement>(null);
    const [yardiForm, setYardiForm] = useState<YardiFormState>({
        propertyId: '',
        username: '',
        password: '',
        serverName: '',
        beansPath: '',
        transactionSource: '',
        agentFirstName: '',
        agentLastName: '',
    });

    const [entrataForm, setEntrataForm] = useState<EntrataFormState>({
        entrataURL: '',
        username: '',
        password: '',
        propertyId: '',
        originatingSourceId: '',
        entrataName: '',
    });

    const [realpageForm, setRealpageForm] = useState<RealpageFormState>({
        leasingAgentId: '',
        pmcId: '',
        siteId: '',
        buildingId: '',
        l2lPropertyId: '',
        ilmPropertyId: '',
        marketingSourceId: '',
        knockCommunityId: '',
        knockSourceTitle: '',
        knockAgentUsername: '',
        knockAgentPassword: '',
        knockCompanyId: '',
        knockPropertyId: '',
    });

    const [resmanForm, setResmanForm] = useState<ResmanFormState>({
        accountId: '',
        propertyId: '',
        agentId: '',
        transactionSource: '',
    });

    const [knockForm, setKnockForm] = useState<KnockFormState>({
        knockCommunityId: '',
        knockAgentUserName: '',
        knockSourceTitle: '',
        knockCompanyId: '',
        knockPropertyId: '',
    });

    const [funnelForm, setFunnelForm] = useState<FunnelFormState>({
        funnelCommunityId: '',
    });

    /** Prevents resetting to step 1 when parent re-renders after onSave (so success screen stays visible) */
    const editInitDoneRef = useRef(false);

    /** Trim leading whitespace on input/textarea change (same as RaiseSupportTicket) */
    const handleTrimStartChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        onChange: (value: string) => void
    ) => {
        const trimmedValue = e.target.value.trimStart();
        onChange(trimmedValue);
    };

    /**
     * When opening modal in edit mode, skip step 0 and pre-fill form with backend data.
     * When opening for new connect, reset to initial state.
     * Only run edit init once per open; do not reset after success (so "Successfully Connected!" stays visible).
     */
    useEffect(() => {
        if (editPmsKey && editFormData) {
            if (!editInitDoneRef.current) {
                editInitDoneRef.current = true;
                setPmsType(editPmsKey);
                setStep(1);
                setConnectionStatus('idle');
                if (editPmsKey === 'yardi') {
                    setYardiForm({
                        propertyId: editFormData.propertyId ?? '',
                        username: editFormData.username ?? '',
                        password: editFormData.password ?? '',
                        serverName: editFormData.serverName ?? '',
                        beansPath: editFormData.beansPath ?? '',
                        transactionSource: editFormData.transactionSource ?? '',
                        agentFirstName: editFormData.agentFirstName ?? '',
                        agentLastName: editFormData.agentLastName ?? '',
                    });
                } else if (editPmsKey === 'entrata') {
                    setEntrataForm({
                        entrataURL: editFormData.entrataURL ?? '',
                        username: editFormData.username ?? '',
                        password: editFormData.password ?? '',
                        propertyId: editFormData.propertyId ?? '',
                        originatingSourceId: editFormData.originatingSourceId ?? '',
                        entrataName: editFormData.entrataName ?? '',
                    });
                } else if (editPmsKey === 'realpage') {
                    setRealpageForm({
                        leasingAgentId: editFormData.leasingAgentId ?? '',
                        pmcId: editFormData.pmcId ?? '',
                        siteId: editFormData.siteId ?? '',
                        buildingId: editFormData.buildingId ?? '',
                        l2lPropertyId: editFormData.l2lPropertyId ?? '',
                        ilmPropertyId: editFormData.ilmPropertyId ?? '',
                        marketingSourceId: editFormData.marketingSourceId ?? '',
                        knockCommunityId: '',
                        knockSourceTitle: '',
                        knockAgentUsername: '',
                        knockAgentPassword: '',
                        knockCompanyId: '',
                        knockPropertyId: '',
                    });
                } else if (editPmsKey === 'resman') {
                    setResmanForm({
                        accountId: editFormData.accountId ?? '',
                        propertyId: editFormData.propertyId ?? '',
                        agentId: editFormData.agentId ?? '',
                        transactionSource: editFormData.transactionSource ?? '',
                    });
                } else if (editPmsKey === 'knock') {
                    setKnockForm({
                        knockCommunityId: editFormData.knockCommunityId ?? '',
                        knockAgentUserName: editFormData.knockAgentUserName ?? '',
                        knockSourceTitle: editFormData.knockSourceTitle ?? '',
                        knockCompanyId: editFormData.knockCompanyId ?? '',
                        knockPropertyId: editFormData.knockPropertyId ?? '',
                    });
                } else if (editPmsKey === 'funnel') {
                    setFunnelForm({
                        funnelCommunityId: editFormData.funnelCommunityId ?? '',
                    });
                }
            }
        } else {
            editInitDoneRef.current = false;
            // New connect: reset to initial state
            setStep(0);
            setPmsType(null);
            setConnectionStatus('idle');
            setYardiForm({ propertyId: '', username: '', password: '', serverName: '', beansPath: '', transactionSource: '', agentFirstName: '', agentLastName: '' });
            setEntrataForm({ entrataURL: '', username: '', password: '', propertyId: '', originatingSourceId: '', entrataName: '' });
            setRealpageForm({
                leasingAgentId: '', pmcId: '', siteId: '', buildingId: '', l2lPropertyId: '', ilmPropertyId: '',
                marketingSourceId: '', knockCommunityId: '', knockSourceTitle: '', knockAgentUsername: '', knockAgentPassword: '', knockCompanyId: '', knockPropertyId: '',
            });
            setResmanForm({ accountId: '', propertyId: '', agentId: '', transactionSource: '' });
            setKnockForm({ knockCommunityId: '', knockAgentUserName: '', knockSourceTitle: '', knockCompanyId: '', knockPropertyId: '' });
            setFunnelForm({ funnelCommunityId: '' });
        }
    }, [editPmsKey, editFormData]);

    const closeModal = () => {
        setOpen?.(false);
    };

    const progressPercent = (step / (steps.length - 1)) * 95;

    const canContinue = () => {
        if (step === 0) return Boolean(pmsType);

        if (step === 1) {
            if (pmsType === "yardi") return yardiForm.beansPath.trim().length > 0;
            if (pmsType === "entrata") return entrataForm.entrataURL.trim().length > 0 && entrataForm.username.trim().length > 0 && entrataForm.password.trim().length > 0;
            if (pmsType === "realpage") return realpageForm.pmcId.trim().length > 0 && realpageForm.siteId.trim().length > 0;
            if (pmsType === "resman") return resmanForm.accountId.trim().length > 0 && resmanForm.propertyId.trim().length > 0;
            if (pmsType === "funnel") return funnelForm.funnelCommunityId.trim().length > 0;
            if (pmsType === "knock") {
                const k = knockForm;
                return (k.knockCommunityId?.trim().length ?? 0) > 0 &&
                    (k.knockCompanyId?.trim().length ?? 0) > 0 &&
                    (k.knockPropertyId?.trim().length ?? 0) > 0;
            }
            return false;
        }

        return true;
    };

    const handleBack = () => {
        if (step === 0) return;
        setStep((prev) => (prev === 0 ? 0 : ((prev - 1) as Step)));
    };


    type ConnectionStatus = 'idle' | 'success' | 'failed';

    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('idle');
    const [isConnecting, setIsConnecting] = useState(false);

    const handleContinue = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!canContinue()) return;

        // If going to Sync step
        if (step === 1) {
            setStep(2);
            setIsConnecting(true);

            const trim = (val: string) => trimData<string>(val) || '';

            try {
                if (pmsType === 'yardi') {
                    const payload: ConnectPmsPayload = {
                        yardiPropertyId: trim(yardiForm.propertyId),
                        yardiUserName: trim(yardiForm.username),
                        yardiPassword: trim(yardiForm.password),
                        yardiServerName: trim(yardiForm.serverName),
                        yardiTransactionSource: trim(yardiForm.transactionSource),
                        yardiBaseUrl: trim(yardiForm.beansPath),
                        yardiAgentFirstName: trim(yardiForm.agentFirstName),
                        yardiAgentLastName: trim(yardiForm.agentLastName),
                    };
                    await dispatch(connectPms('yardi', payload, locationId || ''));
                } else if (pmsType === 'entrata') {
                    const payload: ConnectPmsPayload = {
                        entrataUserName: trim(entrataForm.username),
                        entrataPassword: trim(entrataForm.password),
                        entrataPropertyId: trim(entrataForm.propertyId),
                        entrataSubDomainName: trim(entrataForm.entrataName),
                        entrataUrl: trim(entrataForm.entrataURL),
                        originationSourceId: trim(entrataForm.originatingSourceId),
                    };
                    await dispatch(connectPms('entrata', payload, locationId || ''));
                } else if (pmsType === 'realpage') {
                    const payload: ConnectPmsPayload = {
                        buildingId: trim(realpageForm.buildingId),
                        pineappleLeasingAgentID: trim(realpageForm.leasingAgentId),
                        realPageILMPropertyId: trim(realpageForm.ilmPropertyId),
                        realPageL2LPropertyId: trim(realpageForm.l2lPropertyId),
                        realPagePmcID: trim(realpageForm.pmcId),
                        realPageSiteID: trim(realpageForm.siteId),
                        realpageMarketingSourceId: trim(realpageForm.marketingSourceId),
                    };
                    await dispatch(connectPms('realpage', payload, locationId || ''));
                } else if (pmsType === 'resman') {
                    const payload: ConnectPmsPayload = {
                        resmanAccountId: trim(resmanForm.accountId),
                        resmanPropertyId: trim(resmanForm.propertyId),
                        resmanAgentId: trim(resmanForm.agentId),
                        resmanTransactionSource: trim(resmanForm.transactionSource),
                    };
                    await dispatch(connectPms('resman', payload, locationId || ''));
                } else if (pmsType === 'knock') {
                    const knockPayload: ConnectPmsKnockPayload = {
                        knockCommunityId: trim(knockForm.knockCommunityId),
                        knockAgentUserName: trim(knockForm.knockAgentUserName),
                        knockSourceTitle: trim(knockForm.knockSourceTitle),
                        knockCompanyId: trim(knockForm.knockCompanyId),
                        knockPropertyId: trim(knockForm.knockPropertyId),
                    };
                    await dispatch(connectPms('knock', knockPayload, locationId || ''));
                } else if (pmsType === 'funnel') {
                    const payload: ConnectPmsPayload = {
                        funnelCommunityId: trim(funnelForm.funnelCommunityId),
                    };
                    await dispatch(connectPms('funnel', payload, locationId || ''));
                }
                setConnectionStatus('success');
                if (pmsType) {
                    const trimmedFormData =
                        pmsType === 'yardi'
                            ? {
                                propertyId: trim(yardiForm.propertyId),
                                username: trim(yardiForm.username),
                                password: trim(yardiForm.password),
                                serverName: trim(yardiForm.serverName),
                                transactionSource: trim(yardiForm.transactionSource),
                                beansPath: trim(yardiForm.beansPath),
                                agentFirstName: trim(yardiForm.agentFirstName),
                                agentLastName: trim(yardiForm.agentLastName),
                            }
                            : pmsType === 'entrata'
                                ? {
                                    entrataURL: trim(entrataForm.entrataURL),
                                    username: trim(entrataForm.username),
                                    password: trim(entrataForm.password),
                                    propertyId: trim(entrataForm.propertyId),
                                    originatingSourceId: trim(entrataForm.originatingSourceId),
                                    entrataName: trim(entrataForm.entrataName),
                                }
                                : pmsType === 'realpage'
                                    ? {
                                        leasingAgentId: trim(realpageForm.leasingAgentId),
                                        pmcId: trim(realpageForm.pmcId),
                                        siteId: trim(realpageForm.siteId),
                                        buildingId: trim(realpageForm.buildingId),
                                        l2lPropertyId: trim(realpageForm.l2lPropertyId),
                                        ilmPropertyId: trim(realpageForm.ilmPropertyId),
                                        marketingSourceId: trim(realpageForm.marketingSourceId),
                                        knockCommunityId: trim(realpageForm.knockCommunityId),
                                        knockSourceTitle: trim(realpageForm.knockSourceTitle),
                                        knockAgentUsername: trim(realpageForm.knockAgentUsername),
                                        knockAgentPassword: trim(realpageForm.knockAgentPassword),
                                        knockCompanyId: trim(realpageForm.knockCompanyId),
                                        knockPropertyId: trim(realpageForm.knockPropertyId),
                                    }
                                    : pmsType === 'knock'
                                        ? {
                                            knockCommunityId: trim(knockForm.knockCommunityId),
                                            knockAgentUserName: trim(knockForm.knockAgentUserName),
                                            knockSourceTitle: trim(knockForm.knockSourceTitle),
                                            knockCompanyId: trim(knockForm.knockCompanyId),
                                            knockPropertyId: trim(knockForm.knockPropertyId),
                                        }
                                        : pmsType === 'funnel'
                                            ? {
                                                funnelCommunityId: trim(funnelForm.funnelCommunityId),
                                            }
                                            : {
                                                accountId: trim(resmanForm.accountId),
                                                propertyId: trim(resmanForm.propertyId),
                                                agentId: trim(resmanForm.agentId),
                                                transactionSource: trim(resmanForm.transactionSource),
                                            };
                    onSave?.({
                        pmsType,
                        formData: trimmedFormData,
                        knockData:
                            pmsType === 'knock'
                                ? {
                                    knockCommunityId: trim(knockForm.knockCommunityId),
                                    knockAgentUserName: trim(knockForm.knockAgentUserName),
                                    knockSourceTitle: trim(knockForm.knockSourceTitle),
                                    knockCompanyId: trim(knockForm.knockCompanyId),
                                    knockPropertyId: trim(knockForm.knockPropertyId),
                                }
                                : null,
                    });
                    setTimeout(() => {
                        closeModal();
                    }, 1000);
                }
            } catch {
                setConnectionStatus('failed');
                if (locationId) {
                    dispatch(getLocationById(locationId));
                }
            } finally {
                setIsConnecting(false);
            }

            return;
        }

        if (step === 2) {
            closeModal();
            return;
        }

        setStep((prev) => ((prev + 1) as Step));
    };


    const renderKnockForm = () => (
        <form className={styles.stepContent}>
            <div className={styles.stepHeader}>
                <h2 className={styles.sectionTitle}>Configure Knock CRM</h2>
                <p className={styles.sectionSubtitle}>
                    Enter your Knock CRM credentials for lead management and property marketing.
                </p>
            </div>
            <div className={styles.grid2}>
                <div className={styles.fieldWrapper}>
                    <label>Knock Community ID</label>
                    <input
                        value={knockForm.knockCommunityId}
                        onChange={(e) => handleTrimStartChange(e, (value) => setKnockForm((p) => ({ ...p, knockCommunityId: value })))}

                        placeholder="Knock Community ID"
                    />
                </div>
                <div className={styles.fieldWrapper}>
                    <label>Knock Agent Username</label>
                    <input
                        value={knockForm.knockAgentUserName}
                        onChange={(e) => handleTrimStartChange(e, (value) => setKnockForm((p) => ({ ...p, knockAgentUserName: value })))}
                        placeholder="Knock Agent Username"
                    />
                </div>
                <div className={styles.fieldWrapper}>
                    <label>Knock Source Title (Optional)</label>
                    <input
                        value={knockForm.knockSourceTitle}
                        onChange={(e) => handleTrimStartChange(e, (value) => setKnockForm((p) => ({ ...p, knockSourceTitle: value })))}
                        placeholder="Knock Source Title"
                    />
                </div>
                <div className={styles.fieldWrapper}>
                    <label>Knock Company Id</label>
                    <input
                        value={knockForm.knockCompanyId}
                        onChange={(e) => handleTrimStartChange(e, (value) => setKnockForm((p) => ({ ...p, knockCompanyId: value })))}
                        placeholder="Knock Company Id"
                    />
                </div>
                <div className={styles.fieldWrapper}>
                    <label>Knock Property Id</label>
                    <input
                        value={knockForm.knockPropertyId}
                        onChange={(e) => handleTrimStartChange(e, (value) => setKnockForm((p) => ({ ...p, knockPropertyId: value })))}
                        placeholder="Knock Property Id"
                    />
                </div>
            </div>
        </form>
    );

    const pmsTypeDisplayName: Record<PmsType, string> = {
        yardi: 'Yardi',
        entrata: 'Entrata',
        realpage: 'RealPage OneSite',
        resman: 'ResMan',
        knock: 'Knock',
        funnel: 'Funnel',
    };

    const renderStepContent = () => {
        if (step === 0) {
            const options: { key: PmsType; icon: string | React.ReactNode; label: string, description: string }[] = [
                { key: 'yardi', icon: <YardiPMSIcon />, label: 'Yardi', description: 'Property management for leasing, accounting, and operations.' },
                { key: 'entrata', icon: <EntrataPMSIcon />, label: 'Entrata', description: 'All-in-one platform for leasing and property operations.' },
                { key: 'realpage', icon: <RealPagePMSIcon />, label: 'RealPage OneSite', description: 'Data-driven tools for leasing and property management.' },
                { key: 'resman', icon: <ResmanPMSIcon />, label: 'ResMan', description: 'Simplified property management for leasing and operations' },
                { key: 'knock', icon: <KnockPMSIcon />, label: 'Knock', description: 'CRM for lead management and property marketing.' },
                { key: 'funnel', icon: <FunnelPMSIcon />, label: 'Funnel', description: 'PMS integration for Funnel property data.' },
            ];

            return (
                <div className={styles.stepContent}>
                    <div className={styles.stepHeader}>
                        <h2 className={styles.sectionTitle}>Select Your PMS/CRM Provider</h2>
                        <p className={styles.sectionSubtitle}>Choose the property management system you currently use to manage your properties</p>
                    </div>
                    <div className={styles.pmsGrid} role="radiogroup" aria-label="Select PMS Provider">
                        {options.map((opt) => {
                            const active = pmsType === opt.key;
                            const isConnected = connectedPmsKeys.includes(opt.key);
                            return (
                                <button
                                    key={opt.key}
                                    type="button"
                                    role="radio"
                                    aria-checked={active}
                                    aria-disabled={isConnected}
                                    disabled={isConnected}
                                    className={`${styles.pmsCard} ${active ? styles.pmsCardActive : ''} ${isConnected ? styles.pmsCardDisabled : ''}`}
                                    onClick={() => !isConnected && setPmsType(opt.key)}>
                                    {typeof opt.icon === 'string' ? <img src={opt.icon} alt={opt.label} width={48} height={48} /> : opt.icon}
                                    <span className={styles.pmsCardTitle}>{opt.label}</span>
                                    <p className={styles.pmsCardDescription}>{opt.description}</p>
                                </button>
                            );
                        })}
                    </div>
                </div>
            );
        }

        if (step === 1) {

            return (
                <>
                    {/* yardi */}
                    {pmsType === 'yardi' && (
                        <form className={styles.stepContent}>
                            <div className={styles.stepHeader}>
                                <h2 className={styles.sectionTitle}>Configure Yardi PMS</h2>
                                <p className={styles.sectionSubtitle}>
                                    Enter your API credentials to establish a secure connection with your Yardi PMS.
                                </p>
                            </div>
                            <div className={styles.grid2}>
                                <div className={styles.fieldWrapper}>
                                    <label>Yardi Property ID</label>
                                    <input
                                        value={yardiForm.propertyId}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setYardiForm((p) => ({ ...p, propertyId: value })))}
                                        placeholder="Yardi Property ID"
                                    />
                                </div>
                                <div className={styles.fieldWrapper}>
                                    <label>Yardi User Name</label>
                                    <input
                                        value={yardiForm.username}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setYardiForm((p) => ({ ...p, username: value })))}
                                        placeholder="Yardi User Name"
                                    />
                                </div>
                                <div className={styles.fieldWrapper}>
                                    <label>Yardi Password</label>
                                    <input

                                        type={showPass ? 'text' : 'password'}
                                        value={yardiForm.password}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setYardiForm((p) => ({ ...p, password: value })))}
                                        placeholder="Yardi Password"
                                        ref={passwordRef}
                                    />
                                    <div className={styles.eyeToggler} onClick={() => setShowPass(!showPass)}>
                                        <img src={!showPass ? eyeClose : eyeOpen} alt="Eye" />
                                    </div>
                                </div>

                                <div className={styles.fieldWrapper}>
                                    <label>Yardi Server Name/Database</label>
                                    <input
                                        value={yardiForm.serverName}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setYardiForm((p) => ({ ...p, serverName: value })))}
                                        placeholder="Yardi Server Name/Database"
                                    />
                                </div>
                                <div className={styles.fieldWrapper}>
                                    <label>Yardi Transaction Source</label>
                                    <input
                                        value={yardiForm.transactionSource}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setYardiForm((p) => ({ ...p, transactionSource: value })))}
                                        placeholder="Yardi Transaction Source"
                                    />
                                </div>
                                <div className={styles.fieldWrapper}>
                                    <label>
                                        Yardi Base URL
                                    </label>
                                    <input
                                        value={yardiForm.beansPath}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setYardiForm((p) => ({ ...p, beansPath: value })))}
                                        placeholder="Enter URL"
                                    />
                                </div>
                                <div className={styles.fieldWrapper}>
                                    <label>Agent First Name</label>
                                    <input
                                        value={yardiForm.agentFirstName}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setYardiForm((p) => ({ ...p, agentFirstName: value })))}
                                        placeholder="Agent First Name"
                                    />
                                </div>
                                <div className={styles.fieldWrapper}>
                                    <label>Agent Last Name</label>
                                    <input
                                        value={yardiForm.agentLastName}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setYardiForm((p) => ({ ...p, agentLastName: value })))}
                                        placeholder="Agent Last Name"
                                    />
                                </div>
                            </div>
                        </form>
                    )
                    }

                    {/* entrata */}
                    {pmsType === 'entrata' && (
                        <form className={styles.stepContent}>
                            <div className={styles.stepHeader}>
                                <h2 className={styles.sectionTitle}>Configure Entrata PMS</h2>
                                <p className={styles.sectionSubtitle}>
                                    Enter your API credentials to establish a secure connection with your Entrata PMS.
                                </p>
                            </div>
                            <div className={styles.grid2}>
                                <div className={styles.fieldWrapper}>
                                    <label>Entrata URL</label>
                                    <input
                                        value={entrataForm.entrataURL}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setEntrataForm((p) => ({ ...p, entrataURL: value })))}
                                        placeholder="Entrata URL"
                                    />
                                </div>
                                <div className={styles.fieldWrapper}>
                                    <label>Entrata User Name</label>
                                    <input
                                        value={entrataForm.username}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setEntrataForm((p) => ({ ...p, username: value })))}
                                        placeholder="Entrata User Name"
                                    />
                                </div>
                                <div className={styles.fieldWrapper}>
                                    <label>Entrata Password</label>
                                    <input
                                        type={showPass ? 'text' : 'password'}
                                        value={entrataForm.password}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setEntrataForm((p) => ({ ...p, password: value })))}
                                        placeholder="Entrata Password"
                                        ref={passwordRef}
                                    />
                                    <div className={styles.eyeToggler} onClick={() => setShowPass(!showPass)}>
                                        <img src={!showPass ? eyeClose : eyeOpen} alt="Eye" />
                                    </div>
                                </div>

                                <div className={styles.fieldWrapper}>
                                    <label>Entrata Property Id</label>
                                    <input
                                        value={entrataForm.propertyId}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setEntrataForm((p) => ({ ...p, propertyId: value })))}
                                        placeholder="Entrata Property Id"
                                    />
                                </div>
                                <div className={styles.fieldWrapper}>
                                    <label>Originating Lead Source ID</label>
                                    <input
                                        value={entrataForm.originatingSourceId}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setEntrataForm((p) => ({ ...p, originatingSourceId: value })))}
                                        placeholder="Originating Lead Source ID"
                                    />
                                </div>
                                <div className={styles.fieldWrapper}>
                                    <label>
                                        Entrata Sub Domain Name
                                    </label>
                                    <input
                                        value={entrataForm.entrataName}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setEntrataForm((p) => ({ ...p, entrataName: value })))}
                                        placeholder="Entrata Sub Domain Name"
                                    />
                                </div>

                            </div>
                        </form>
                    )
                    }
                    {/* realpage */}
                    {pmsType === 'realpage' && (
                        <form className={styles.stepContent}>
                            <div className={styles.stepHeader}>
                                <h2 className={styles.sectionTitle}>Configure RealPage PMS</h2>
                                <p className={styles.sectionSubtitle}>
                                    Enter your API credentials to establish a secure connection with your RealPage OneSite PMS.
                                </p>
                            </div>
                            <div className={styles.grid2}>
                                <div className={styles.fieldWrapper}>
                                    <label>Pineapple Leasing Agent ID</label>
                                    <input
                                        value={realpageForm.leasingAgentId}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setRealpageForm((p) => ({ ...p, leasingAgentId: value })))}
                                        placeholder="Pineapple Leasing Agent ID"
                                    />
                                </div>
                                <div className={styles.fieldWrapper}>
                                    <label>RealPage Pmc Id</label>
                                    <input
                                        value={realpageForm.pmcId}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setRealpageForm((p) => ({ ...p, pmcId: value })))}
                                        placeholder="RealPage Pmc Id"
                                    />
                                </div>
                                <div className={styles.fieldWrapper}>
                                    <label>RealPage Site Id</label>
                                    <input
                                        value={realpageForm.siteId}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setRealpageForm((p) => ({ ...p, siteId: value })))}
                                        placeholder="RealPage Site Id"
                                    />
                                </div>
                                <div className={styles.fieldWrapper}>
                                    <label>RealPage Building Id</label>
                                    <input
                                        value={realpageForm.buildingId}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setRealpageForm((p) => ({ ...p, buildingId: value })))}
                                        placeholder="RealPage Building Id"
                                    />
                                </div>
                                <div className={styles.fieldWrapper}>
                                    <label>RealPage L2L Property Id</label>
                                    <input
                                        value={realpageForm.l2lPropertyId}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setRealpageForm((p) => ({ ...p, l2lPropertyId: value })))}
                                        placeholder="RealPage L2L Property Id"
                                    />
                                </div>
                                <div className={styles.fieldWrapper}>
                                    <label>RealPage ILM Property Id</label>
                                    <input
                                        value={realpageForm.ilmPropertyId}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setRealpageForm((p) => ({ ...p, ilmPropertyId: value })))}
                                        placeholder="RealPage ILM Property Id"
                                    />
                                </div>
                                <div className={styles.fieldWrapper}>
                                    <label>Marketing Source ID</label>
                                    <input
                                        value={realpageForm.marketingSourceId}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setRealpageForm((p) => ({ ...p, marketingSourceId: value })))}
                                        placeholder="Marketing Source ID"
                                    />
                                </div>
                            </div>
                        </form>
                    )
                    }

                    {/* resman */}
                    {
                        pmsType === 'resman' && (

                            <form className={styles.stepContent}>
                                <div className={styles.stepHeader}>
                                    <h2 className={styles.sectionTitle}>Configure ResMan PMS</h2>
                                    <p className={styles.sectionSubtitle}>
                                        Enter your API credentials to establish a secure connection with your ResMan PMS.
                                    </p>
                                </div>
                                <div className={styles.grid2}>
                                    <div className={styles.fieldWrapper}>
                                        <label>Account ID</label>
                                        <input
                                            value={resmanForm.accountId}
                                            onChange={(e) => handleTrimStartChange(e, (value) => setResmanForm((p) => ({ ...p, accountId: value })))}
                                            placeholder="Account ID"
                                        />
                                    </div>
                                    <div className={styles.fieldWrapper}>
                                        <label>Property ID</label>
                                        <input
                                            value={resmanForm.propertyId}
                                            onChange={(e) => handleTrimStartChange(e, (value) => setResmanForm((p) => ({ ...p, propertyId: value })))}
                                            placeholder="Property ID"
                                        />
                                    </div>
                                    <div className={styles.fieldWrapper}>
                                        <label>Agent ID</label>
                                        <input
                                            value={resmanForm.agentId}
                                            onChange={(e) => handleTrimStartChange(e, (value) => setResmanForm((p) => ({ ...p, agentId: value })))}
                                            placeholder="Agent ID"
                                        />
                                    </div>
                                    <div className={styles.fieldWrapper}>
                                        <label>Transaction Source</label>
                                        <input
                                            type="text"
                                            value={resmanForm.transactionSource}
                                            onChange={(e) => handleTrimStartChange(e, (value) => setResmanForm((p) => ({ ...p, transactionSource: value })))}
                                            placeholder="Transaction Source"
                                        />
                                    </div>


                                </div>
                            </form>
                        )
                    }

                    {/* knock */}
                    {pmsType === 'knock' && renderKnockForm()}

                    {/* funnel */}
                    {pmsType === 'funnel' && (
                        <form className={styles.stepContent}>
                            <div className={styles.stepHeader}>
                                <h2 className={styles.sectionTitle}>Configure Funnel PMS</h2>
                                <p className={styles.sectionSubtitle}>
                                    Enter your Funnel ID to establish a connection with Funnel.
                                </p>
                            </div>
                            <div className={styles.grid2}>
                                <div className={styles.fieldWrapper}>
                                    <label>Funnel ID</label>
                                    <input
                                        value={funnelForm.funnelCommunityId}
                                        onChange={(e) => handleTrimStartChange(e, (value) => setFunnelForm((p) => ({ ...p, funnelCommunityId: value })))}
                                        placeholder="Funnel ID"
                                    />
                                </div>
                            </div>
                        </form>
                    )}
                </>
            );
        }

        if (step === 2) {
            if (isConnecting) {
                const connectingLabel = pmsType ? pmsTypeDisplayName[pmsType] : 'PMS';
                return (
                    <div className={styles.loadingContainer}>
                        <img className={styles.loadingIcon} src={loadingConnecting} alt="loadingConnecting" width={96} height={96} />
                        <h2 className={styles.successTitle}>Connecting to {connectingLabel} {pmsType === 'knock' ? 'CRM' : 'PMS'}</h2>
                        <p className={styles.successSubtitle}>
                            Please wait while we verify your credentials and establish a secure connection...
                        </p>
                        <ul className={styles.listDisc}>
                            <li><span><img src={checkDone} alt="Success" width={18} height={18} /></span><p>Validating credentials</p></li>
                            <li><span><img src={checkDone} alt="Success" width={18} height={18} /></span><p>Establishing secure connection</p></li>
                            <li><span><img src={loadingDone} alt="Success" width={18} height={18} /></span><p>Syncing property data</p></li>
                        </ul>
                    </div>
                );
            }

            if (connectionStatus === 'success') {
                return (
                    <div className={styles.successContainer}>
                        <div className={styles.successIconCircle}>
                            <img src={successIcon} alt="Success" width={96} height={96} />
                        </div>
                        <h2 className={styles.successTitle}>Successfully Connected!</h2>
                        <p className={styles.successSubtitle}>
                            Your {pmsType
                                ? `${pmsTypeDisplayName[pmsType]} ${pmsType === 'knock' ? 'CRM' : 'PMS'}`
                                : 'PMS'} is now active and ready to sync data.
                            Your property data will automatically sync every hour.
                        </p>
                    </div>
                );
            }

            if (connectionStatus === 'failed') {
                return (
                    <div className={styles.successContainer}>
                        <div className={styles.successIconCircle}>
                            <img src={failedIcon} alt="Failed" width={96} height={96} />
                        </div>
                        <h2 className={styles.failedTitle}>Connection Failed</h2>
                        <p className={styles.successSubtitle}>
                            We couldn’t connect to your {pmsType
                                ? `${pmsTypeDisplayName[pmsType]} ${pmsType === 'knock' ? 'CRM' : 'PMS'}`
                                : 'PMS'} at the moment. Please check your details and try again, or raise a support ticket if the issue continues.
                        </p>

                    </div>
                );
            }
        }

    };

    return (
        <div className={styles.modalAddStaff}>


            <div className={styles.modalHeader}>
                <div className={styles.modalTitle}>
                    <h4>{`Configure PMS/CRM`}</h4>
                </div>
                <span
                    className={styles.close}
                    onClick={closeModal}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') closeModal();
                    }}
                    role="button"
                    tabIndex={0}>
                    <img src={iconClose} alt="Close" />
                </span>
            </div>
            <div className={styles.modalBody}>
                <form className={styles.form} onSubmit={handleContinue}>
                    <div className={styles.stepper} aria-label="PMS configuration steps">
                        <div className={styles.stepperLineContainer}>
                            <div className={styles.stepperLine} />
                            <div className={styles.stepperLineActive} style={{ width: `${progressPercent}%` }} />
                        </div>

                        {steps.map((label, idx) => {
                            const isCompleted = idx < step;
                            const isActive = idx === step;
                            return (
                                <div
                                    className={`${styles.step} 
                                    ${idx === 0 ? styles.firstStep : ''} 
                                    ${idx === steps.length - 1 ? styles.lastStep : ''}`}
                                >

                                    <div className={`${styles.stepLabel} ${idx <= step ? styles.stepLabelActive : ''}`}>{label}</div>
                                    <div
                                        className={`${styles.stepDot} ${isCompleted ? styles.stepDotCompleted : ''} ${isActive ? styles.stepDotActive : ''
                                            }`}
                                        aria-current={isActive ? 'step' : undefined}
                                    />
                                </div>
                            );
                        })}
                    </div>

                    {renderStepContent()}

                    <div className={styles.footer}>
                        {step === 1 && (
                            <Button type="button" variant="outlined-green" onClick={handleBack}>
                                Back
                            </Button>
                        )}

                        {/* Normal Continue / Complete button */}
                        {step !== 2 && (
                            <Button type="submit" variant="green" disabled={!canContinue()}>
                                Continue
                            </Button>
                        )}

                        {/* Show only when connection failed */}
                        {step === 2 && connectionStatus === 'failed' && !isConnecting && (
                            <div className={styles.btnWrapBottom}>
                                <Button
                                    type="button"
                                    variant="outlined-red"
                                    onClick={() => {
                                        navigate('/support-ticket');
                                    }}
                                >
                                    Raise Support Ticket
                                </Button>

                                <Button
                                    type="button"
                                    variant="red"
                                    onClick={() => {
                                        setConnectionStatus('idle');
                                        setStep(1);
                                    }}
                                >
                                    Retry
                                </Button>
                            </div>
                        )}
                    </div>
                </form>
            </div>
        </div>
    )
}

export default PMSConfiguaration;
